<template>
    <div class="pb-10">
        <PageTitle title="Retraits de stock" :description="`${total || 0} retrait(s) au total`">
            <template #actions>
                <BaseButton variant="primary" @click="onNewWithdrawal">
                    <template #icon-left>
                        <i class="fa-solid fa-plus"></i>
                    </template>
                    Nouveau retrait
                </BaseButton>
            </template>
        </PageTitle>

        <!-- Filtres -->
        <BaseCard class="mb-6">
            <div class="flex flex-wrap gap-4 items-end">
                <div class="flex-1 min-w-[200px]">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Date début</label>
                    <BaseInput v-model="startDate" type="date" @input="onDateChange" />
                </div>
                <div class="flex-1 min-w-[200px]">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Date fin</label>
                    <BaseInput v-model="endDate" type="date" @input="onDateChange" />
                </div>
                <div class="flex-1 min-w-[180px]">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                    <select v-model="selectedStatus" @change="onStatusChange"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm">
                        <option value="">Tous les statuts</option>
                        <option value="draft">Brouillon</option>
                        <option value="completed">Complété</option>
                        <option value="cancelled">Annulé</option>
                    </select>
                </div>
                <div class="flex-1 min-w-[180px]">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Raison</label>
                    <select v-model="selectedReason" @change="onReasonChange"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm">
                        <option value="">Toutes les raisons</option>
                        <option value="damage">Dommage/Casse</option>
                        <option value="loss">Perte</option>
                        <option value="theft">Vol</option>
                        <option value="expired">Expiré</option>
                        <option value="return_to_supplier">Retour fournisseur</option>
                        <option value="adjustment">Ajustement</option>
                        <option value="other">Autre</option>
                    </select>
                </div>
                <div class="flex-1 min-w-[250px]">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
                    <BaseInput v-model="searchQuery" type="search" placeholder="Rechercher par référence..." clearable
                        @input="onSearchChange">
                        <template #icon-left>
                            <i class="fa-solid fa-search text-gray-400"></i>
                        </template>
                    </BaseInput>
                </div>
                <BaseButton v-if="hasActiveFilters" variant="ghost" @click="clearAllFilters">
                    <template #icon-left>
                        <i class="fa-solid fa-times"></i>
                    </template>
                    Réinitialiser
                </BaseButton>
            </div>
        </BaseCard>

        <!-- Loader -->
        <BaseCard v-if="loading">
            <div class="flex flex-col items-center justify-center py-20">
                <div class="w-10 h-10 border-4 border-gray-200 border-t-primary rounded-full animate-spin mb-4"></div>
                <p class="text-gray-600">Chargement des retraits...</p>
            </div>
        </BaseCard>

        <!-- Empty state -->
        <BaseCard v-else-if="!loading && withdrawals.length === 0">
            <div class="flex flex-col items-center justify-center py-20 text-center">
                <div class="w-16 h-16 text-gray-300 mb-4">
                    <i class="fa-solid fa-box text-6xl"></i>
                </div>
                <h3 class="text-lg font-semibold text-gray-900 mb-2">Aucun retrait trouvé</h3>
                <p class="text-gray-600 max-w-md mb-6">
                    <span v-if="hasActiveFilters">Aucun retrait ne correspond à vos critères.</span>
                    <span v-else>Aucun retrait enregistré.</span>
                </p>
                <BaseButton v-if="hasActiveFilters" variant="primary" @click="clearAllFilters">
                    Réinitialiser les filtres
                </BaseButton>
                <BaseButton v-else variant="primary" @click="onNewWithdrawal">
                    <template #icon-left>
                        <i class="fa-solid fa-plus"></i>
                    </template>
                    Nouveau retrait
                </BaseButton>
            </div>
        </BaseCard>

        <!-- Liste des retraits -->
        <BaseCard v-else>
            <div class="overflow-x-auto">
                <table class="w-full border-collapse">
                    <thead>
                        <tr class="bg-gray-50 border-b-2 border-gray-200">
                            <th
                                class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[180px]">
                                Référence
                            </th>
                            <th
                                class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[150px]">
                                Date
                            </th>
                            <th
                                class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[160px]">
                                Raison
                            </th>
                            <th
                                class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[120px]">
                                Statut
                            </th>
                            <th
                                class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[140px]">
                                Éléments
                            </th>
                            <th
                                class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[160px]">
                                Créé par
                            </th>
                            <th
                                class="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[150px]">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr v-for="withdrawal in withdrawals" :key="withdrawal.id"
                            class="hover:bg-gray-50 transition-colors cursor-pointer" @click="viewDetails(withdrawal)">
                            <td class="px-4 py-4">
                                <router-link :to="{ name: 'stock-withdrawal-details', params: { id: withdrawal.id } }"
                                    @click.stop class="text-primary font-semibold hover:underline">
                                    {{ withdrawal.reference }}
                                </router-link>
                            </td>
                            <td class="px-4 py-4">
                                <span class="text-sm text-gray-900">{{ formatDate(withdrawal.withdrawal_date) }}</span>
                            </td>
                            <td class="px-4 py-4">
                                <span :class="[
                                    'inline-flex px-3 py-1 rounded-full text-xs font-semibold uppercase',
                                    getReasonBadgeClass(withdrawal.reason)
                                ]">
                                    {{ getReasonLabel(withdrawal.reason) }}
                                </span>
                            </td>
                            <td class="px-4 py-4">
                                <span :class="[
                                    'inline-flex px-3 py-1 rounded-full text-xs font-semibold uppercase',
                                    getStatusBadgeClass(withdrawal.status)
                                ]">
                                    {{ getStatusLabel(withdrawal.status) }}
                                </span>
                            </td>
                            <td class="px-4 py-4">
                                <div class="flex flex-col gap-1">
                                    <span class="text-sm font-medium text-gray-900">
                                        {{ withdrawal.items?.length || 0 }} article(s)
                                    </span>
                                    <span class="text-xs text-gray-600">
                                        {{ calculateTotalQuantity(withdrawal.items) }} unité(s)
                                    </span>
                                </div>
                            </td>
                            <td class="px-4 py-4">
                                <span class="text-sm text-gray-900">{{ withdrawal.user?.name || '-' }}</span>
                            </td>
                            <td class="px-4 py-4">
                                <div class="flex items-center justify-center gap-2" @click.stop>
                                    <BaseButton variant="icon" size="sm" @click="downloadPdf(withdrawal)"
                                        :loading="downloadingPdf[withdrawal.id]" title="Télécharger PDF">
                                        <i class="fa-solid fa-file-pdf text-red-600"></i>
                                    </BaseButton>
                                    <BaseButton variant="icon" size="sm" @click="downloadCsv(withdrawal)"
                                        :loading="downloadingCsv[withdrawal.id]" title="Télécharger CSV">
                                        <i class="fa-solid fa-file-text text-blue-600"></i>
                                    </BaseButton>
                                    <BaseButton variant="icon" size="sm" @click="downloadExcel(withdrawal)"
                                        :loading="downloadingExcel[withdrawal.id]" title="Télécharger Excel">
                                        <i class="fa-solid fa-file-excel text-green-600"></i>
                                    </BaseButton>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="!loading && withdrawals.length > 0" class="border-t border-gray-200">
                <PaginationComponent :current-page="currentPage" :per-page="perPage" :total="total" :from="from"
                    :to="to" :show-page-numbers="true" :max-visible-pages="5" @page-change="onPageChange" />
            </div>
        </BaseCard>
    </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import PageTitle from '@/components/common/PageTitle.vue'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import StockWithdrawalService from '@/services/stockWithdrawalService'
import { useToastStore } from '@/store/modules/toastStore'
import { useRouter } from 'vue-router'

export default {
    name: 'StockWithdrawalList',
    components: {
        PaginationComponent,
        PageTitle,
        BaseCard,
        BaseButton,
        BaseInput
    },
    props: {
        perPageDefault: {
            type: Number,
            default: 15
        }
    },
    emits: ['new-withdrawal'],
    setup(props) {
        const toastStore = useToastStore()
        const router = useRouter()
        const withdrawals = ref([])
        const loading = ref(false)
        const startDate = ref('')
        const endDate = ref('')
        const selectedStatus = ref('')
        const selectedReason = ref('')
        const searchQuery = ref('')
        const currentPage = ref(1)
        const perPage = ref(props.perPageDefault)
        const total = ref(0)
        const from = ref(0)
        const to = ref(0)
        const downloadingPdf = ref({})
        const downloadingCsv = ref({})
        const downloadingExcel = ref({})
        const searchTimeout = ref(null)

        const hasActiveFilters = computed(() => {
            return startDate.value || endDate.value || selectedStatus.value || selectedReason.value || searchQuery.value
        })

        const fetchWithdrawals = async () => {
            loading.value = true
            try {
                const params = {
                    page: currentPage.value,
                    per_page: perPage.value
                }

                if (startDate.value) params.start_date = startDate.value
                if (endDate.value) params.end_date = endDate.value
                if (selectedStatus.value) params.status = selectedStatus.value
                if (selectedReason.value) params.reason = selectedReason.value
                if (searchQuery.value) params.search = searchQuery.value

                const response = await StockWithdrawalService.getStockWithdrawals(params)

                // Gérer la structure de réponse de l'API
                if (response.data && response.data.data) {
                    withdrawals.value = response.data.data
                    if (response.data.meta) {
                        currentPage.value = response.data.meta.current_page || currentPage.value
                        perPage.value = response.data.meta.per_page || perPage.value
                        total.value = response.data.meta.total || 0
                        from.value = response.data.meta.from ?? ((response.data.meta.current_page || 1) - 1) * (response.data.meta.per_page || perPage.value) + 1
                        to.value = response.data.meta.to ?? Math.min((response.data.meta.current_page || 1) * (response.data.meta.per_page || perPage.value), response.data.meta.total || 0)
                    }
                } else {
                    withdrawals.value = response.data || []
                }
            } catch (error) {
                console.error('Erreur lors du chargement des retraits:', error)
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement des retraits de stock',
                    duration: 4000
                })
            } finally {
                loading.value = false
            }
        }

        const onDateChange = () => {
            currentPage.value = 1
            fetchWithdrawals()
        }

        const onStatusChange = () => {
            currentPage.value = 1
            fetchWithdrawals()
        }

        const onReasonChange = () => {
            currentPage.value = 1
            fetchWithdrawals()
        }

        const onSearchChange = () => {
            currentPage.value = 1
            clearTimeout(searchTimeout.value)
            searchTimeout.value = setTimeout(() => {
                fetchWithdrawals()
            }, 500)
        }

        const clearAllFilters = () => {
            startDate.value = ''
            endDate.value = ''
            selectedStatus.value = ''
            selectedReason.value = ''
            searchQuery.value = ''
            currentPage.value = 1
            fetchWithdrawals()
        }

        const onPageChange = (page) => {
            currentPage.value = page
            fetchWithdrawals()
        }

        const onNewWithdrawal = () => {
            router.push({ name: 'create-stock-withdrawal' })
        }

        const viewDetails = (withdrawal) => {
            router.push({ name: 'stock-withdrawal-details', params: { id: withdrawal.id } })
        }

        const formatDate = (date) => {
            return StockWithdrawalService.formatDateTime(date)
        }

        const getReasonLabel = (reason) => {
            return StockWithdrawalService.getReasonLabel(reason)
        }

        const getStatusLabel = (status) => {
            return StockWithdrawalService.getStatusLabel(status)
        }

        const getReasonBadgeClass = (reason) => {
            const classes = {
                'damage': 'bg-yellow-100 text-yellow-800',
                'loss': 'bg-yellow-100 text-yellow-800',
                'expired': 'bg-yellow-100 text-yellow-800',
                'theft': 'bg-red-100 text-red-800',
                'return_to_supplier': 'bg-blue-100 text-blue-800',
                'adjustment': 'bg-gray-100 text-gray-800',
                'other': 'bg-gray-100 text-gray-800'
            }
            return classes[reason] || 'bg-gray-100 text-gray-800'
        }

        const getStatusBadgeClass = (status) => {
            const classes = {
                'draft': 'bg-gray-100 text-gray-800',
                'completed': 'bg-green-100 text-green-800',
                'cancelled': 'bg-red-100 text-red-800'
            }
            return classes[status] || 'bg-gray-100 text-gray-800'
        }

        const calculateTotalQuantity = (items) => {
            return StockWithdrawalService.calculateTotalQuantity(items || [])
        }

        const downloadPdf = async (withdrawal) => {
            downloadingPdf.value[withdrawal.id] = true
            try {
                await StockWithdrawalService.downloadStockWithdrawalPdf(withdrawal.id)
                toastStore.addToast({
                    type: 'success',
                    message: 'PDF téléchargé avec succès',
                    duration: 3000
                })
            } catch (error) {
                console.error('Erreur lors du téléchargement PDF:', error)
                toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors du téléchargement PDF',
                    duration: 4000
                })
            } finally {
                downloadingPdf.value[withdrawal.id] = false
            }
        }

        const downloadCsv = async (withdrawal) => {
            downloadingCsv.value[withdrawal.id] = true
            try {
                await StockWithdrawalService.exportStockWithdrawal(withdrawal.id, { format: 'csv' })
                toastStore.addToast({
                    type: 'success',
                    message: 'Fichier CSV téléchargé avec succès',
                    duration: 3000
                })
            } catch (error) {
                console.error('Erreur lors du téléchargement CSV:', error)
                toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors du téléchargement CSV',
                    duration: 4000
                })
            } finally {
                downloadingCsv.value[withdrawal.id] = false
            }
        }

        const downloadExcel = async (withdrawal) => {
            downloadingExcel.value[withdrawal.id] = true
            try {
                await StockWithdrawalService.exportStockWithdrawal(withdrawal.id, { format: 'excel' })
                toastStore.addToast({
                    type: 'success',
                    message: 'Fichier Excel téléchargé avec succès',
                    duration: 3000
                })
            } catch (error) {
                console.error('Erreur lors du téléchargement Excel:', error)
                toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors du téléchargement Excel',
                    duration: 4000
                })
            } finally {
                downloadingExcel.value[withdrawal.id] = false
            }
        }

        onMounted(() => {
            fetchWithdrawals()
        })

        onUnmounted(() => {
            if (searchTimeout.value) {
                clearTimeout(searchTimeout.value)
            }
        })

        return {
            // state
            withdrawals,
            loading,
            startDate,
            endDate,
            selectedStatus,
            selectedReason,
            searchQuery,
            currentPage,
            perPage,
            total,
            from,
            to,
            downloadingPdf,
            downloadingCsv,
            downloadingExcel,
            hasActiveFilters,
            // methods
            onDateChange,
            onStatusChange,
            onReasonChange,
            onSearchChange,
            onPageChange,
            clearAllFilters,
            onNewWithdrawal,
            viewDetails,
            deleteWithdrawal: () => { },
            formatDate,
            getReasonLabel,
            getStatusLabel,
            getReasonBadgeClass,
            getStatusBadgeClass,
            calculateTotalQuantity,
            downloadPdf,
            downloadCsv,
            downloadExcel
        }
    }
}
</script>

<style scoped>
/* Styles minimalistes - la plupart est géré par Tailwind */
</style>
